import { Overlay, type OverlayQuad, type OverlayPositionLink } from './Overlay';

/**
 * Groups several {@link Overlay} instances under one anchor and one lifecycle.
 * The composite owns no quads itself — `emit` fans out to each child. When
 * {@link setTracker} is called, the tracker is propagated to every child so a
 * single call wires up the whole group to follow the same moving entity.
 *
 * Children stay independent re: depth-test mode, color, font, etc.; the
 * composite just bundles them so the caller has one handle to add/remove.
 */
export class CompositeOverlay extends Overlay {
  public readonly children: Overlay[] = [];

  public add<T extends Overlay>(child: T): T {
    this.children.push(child);
    if (this.tracker) child.tracker = this.tracker;
    return child;
  }

  /** Set the tracker on the composite and propagate to every child. */
  public setTracker(tracker: OverlayPositionLink | undefined): void {
    this.tracker = tracker;
    for (const c of this.children) c.tracker = tracker;
  }

  public emit(out: OverlayQuad[]): void {
    for (const c of this.children) {
      if (!c.visible || c.expired) continue;
      c.emit(out);
    }
  }

  public override update(dt: number): void {
    for (const c of this.children) c.update?.(dt);
    // Composite expires only when ALL children have expired — works
    // correctly for long-lived nameplates (none expire) and short-lived
    // damage-number bundles (all expire together) alike.
    if (this.children.length > 0 && this.children.every((c) => c.expired)) {
      this.expired = true;
    }
  }

  public override destroy(): void {
    for (const c of this.children) c.destroy?.();
    this.children.length = 0;
  }
}
